// ** react imports
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES } from '../../../enums/common';

// ** components imports
import NormalButton from '../../../components/common/Buttons/NormalButton/index';

// ** views imports
import QuizTable from '../../../views/quiz/QuizList/QuizTable';
import QuizFilterButton from '../../../views/quiz/QuizList/QuizFilterButton';
import QuizPagination from '../../../views/quiz/QuizList/QuizPagination';

// ** partials imports
import DeleteButton from '../../../partials/actions/DeleteButton';

// ** custom hooks imports
import { useGetAllTemplates } from '../../../hooks/apis/useQuizTemplate';

const ListQuiz = props => {
  // props
  const {} = props;

  // Redux state
  const { token, users } = useSelector(state => state.users);

  // states
  const [selectedItems, setSelectedItems] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    isActive: true, // Only show active templates by default
    search: '',
  });

  // custom hooks
  const {
    data,
    isGetAllTemplatesLoading,
    isGetAllTemplatesFetching,
    getAllTemplates,
  } = useGetAllTemplates();

  // fetch templates whenever query changes
  useEffect(() => {
    // Filter out null/undefined values from query before sending to API
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    getAllTemplates(cleanQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // normalize templates data for table and pagination
  const tableData = useMemo(() => {
    return {
      data: data?.data || [],
      total: data?.total || 0,
      currentPage: query.page,
      totalPages: Math.ceil((data?.total || 0) / query.limit),
    };
  }, [data, query.page, query.limit]);

  // handlers
  const handleSelectedItems = selectedItems => {
    setSelectedItems([...selectedItems]);
  };

  const handleStatusFilter = statusFilters => {
    setQuery(prev => ({
      ...prev,
      ...statusFilters,
      page: 1, // Reset to first page when changing filters
    }));
  };

  const handleSearchFilter = searchTerm => {
    setQuery(prev => ({
      ...prev,
      search: searchTerm,
      page: 1, // Reset to first page when changing filters
    }));
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setQuery(prev => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  const handlePageChange = page => {
    setQuery(prev => ({
      ...prev,
      page,
    }));
  };

  const handleLimitChange = limit => {
    setQuery(prev => ({
      ...prev,
      limit,
      page: 1, // Reset to first page when changing limit
    }));
  };

  const handleResetFilters = () => {
    setQuery({
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      isActive: true,
      search: '',
    });
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Quiz
              </h1>
            </div>

            <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
              <DeleteButton selectedItems={selectedItems} />

              {token && users?.role === USER_ROLES.ADMIN && (
                <Link to="/quiz/add">
                  <NormalButton theme={BUTTON_THEMES.PRIMARY}>
                    <svg
                      className="fill-current shrink-0 xs:hidden"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <svg
                        className="fill-current shrink-0 xs:hidden"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                      >
                        <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                      </svg>
                    </svg>
                    <span className="max-xs:sr-only">New Template</span>
                  </NormalButton>
                </Link>
              )}

              <QuizFilterButton
                align="right"
                onStatusFilter={handleStatusFilter}
                onSearchFilter={handleSearchFilter}
                onReset={handleResetFilters}
                currentFilters={{
                  isActive: query.isActive,
                  isDeleted: query.isDeleted,
                  search: query.search,
                }}
              />
            </div>
          </div>

          {(query.search ||
            query.isDeleted !== undefined ||
            query.isActive !== true) && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {query.search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: &quot;
                          {query.search.length > 20
                            ? query.search.substring(0, 20) + '...'
                            : query.search}
                          &quot;
                        </span>
                      )}
                      {query.isDeleted !== undefined && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Include Deleted: {query.isDeleted ? 'Yes' : 'No'}
                        </span>
                      )}
                      {query.isActive !== true && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Status:{' '}
                          {query.isActive
                            ? 'Active'
                            : query.isActive === false
                              ? 'Inactive'
                              : 'All'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          <QuizTable
            data={tableData}
            selectedItems={handleSelectedItems}
            isGetAllTemplatesFetching={isGetAllTemplatesFetching}
            isGetAllTemplatesLoading={isGetAllTemplatesLoading}
            onSortChange={handleSortChange}
            currentSortBy={query.sortBy}
            currentSortOrder={query.sortOrder}
          />

          <div className="mt-6">
            <QuizPagination
              currentPage={tableData.currentPage}
              totalPages={tableData.totalPages}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              currentLimit={query.limit}
              totalItems={tableData.total}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ListQuiz;
